/**
 * @fileoverview Request interceptor para autenticación automática y refresh preventivo
 * Agrega el token de acceso a cada petición HTTP y renueva tokens próximos a expirar
 * Incluye Request ID único para tracking y logging estructurado
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import type { InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, hasValidToken } from '../utils';
import { shouldRefreshToken, refreshToken } from '../services';
import { generateRequestId, logDebug, logWarn } from '../utils/logger';

/**
 * Interceptor de peticiones que agrega automáticamente el token de autenticación
 * y ejecuta refresh preventivo si el token está próximo a expirar
 *
 * Funcionalidad:
 * - Genera Request ID único para tracking
 * - Verifica si el token necesita renovarse (refresh preventivo)
 * - Renueva el token si es necesario (maneja race conditions con cola)
 * - Verifica si existe un token válido en localStorage
 * - Agrega el header Authorization con Bearer token
 * - Agrega header X-Request-ID para tracking
 * - Solo aplica si hay token disponible
 *
 * @param config - Configuración de la petición axios
 * @returns Configuración modificada con headers de autenticación y tracking
 *
 * @example
 * // El interceptor se aplica automáticamente a todas las peticiones
 * await apiMunzen.get('/endpoint'); // Refresh + Headers automáticos
 */
export const requestInterceptor = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  // Generar Request ID único para tracking
  const requestId = generateRequestId();
  config.headers['X-Request-ID'] = requestId;

  // Log de inicio de petición
  logDebug('Iniciando petición', {
    requestId,
    method: config.method?.toUpperCase(),
    url: config.url,
  });

  // Verificar si existe un token válido
  if (hasValidToken()) {
    // Refresh preventivo si el token está próximo a expirar
    if (shouldRefreshToken()) {
      logDebug('Token próximo a expirar, ejecutando refresh preventivo', { requestId });

      try {
        await refreshToken();
        logDebug('Refresh preventivo completado exitosamente', { requestId });
      } catch (error) {
        // Si el refresh falla, el error handler ya ejecutó logout
        // Permitir que la petición continúe sin token (será rechazada por el servidor)
        logWarn('Refresh preventivo falló, petición continuará sin token', {
          requestId,
          error,
        });
      }
    }

    // Obtener token actualizado (puede ser el nuevo después del refresh)
    const token = getAccessToken();

    if (token) {
      // Agregar header de autenticación
      config.headers.authorization = `Bearer ${token}`;
    }
  }

  return config;
};

/**
 * Manejador de errores del request interceptor
 * Se ejecuta si hay un error antes de enviar la petición
 *
 * @param error - Error ocurrido antes de enviar la petición
 * @returns Promise rechazada con el error
 */
export const requestInterceptorError = (error: unknown): Promise<never> => {
  // Log del error en desarrollo
  if (import.meta.env.DEV) {
    console.error('❌ Error en request interceptor:', error);
  }

  return Promise.reject(error);
};
